import { satisfactionSurveysApi } from '../../services/satisfaction-surveys.api';
import type { SurveyPublicInfoDto } from '../../services/satisfaction-surveys.api';
import { t } from '../../services/i18n';

type State = 'loading' | 'form' | 'answered' | 'submitted' | 'not-found' | 'error';

export class RcSurvey extends HTMLElement {
  private state: State = 'loading';
  private survey: SurveyPublicInfoDto | null = null;
  private selectedRating: number | null = null;
  private token: string | null = null;
  private submitting = false;
  private errorMessage = '';

  connectedCallback(): void {
    this.token = new URLSearchParams(window.location.search).get('token');
    this.render();
    void this.loadSurvey();
  }

  private async loadSurvey(): Promise<void> {
    if (!this.token) {
      this.state = 'not-found';
      this.render();
      return;
    }

    try {
      const survey = await satisfactionSurveysApi.getByToken(this.token);
      if (!survey) {
        this.state = 'not-found';
      } else {
        this.survey = survey;
        this.state = survey.isAnswered ? 'answered' : 'form';
      }
    } catch {
      this.state = 'error';
    }
    this.render();
  }

  private render(): void {
    this.innerHTML = `
      <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:2rem;background:var(--color-bg,#f8fafc);">
        <div style="background:#fff;border-radius:12px;box-shadow:0 2px 16px rgba(0,0,0,.08);padding:2.5rem 2rem;max-width:480px;width:100%;text-align:center;">
          ${this.renderContent()}
        </div>
      </div>`;

    if (this.state === 'form') {
      this.attachFormListeners();
    }
  }

  private renderContent(): string {
    switch (this.state) {
      case 'loading':
        return `<p style="color:var(--color-text-muted,#64748b);">${t('survey.public.loading')}</p>`;

      case 'not-found':
        return `
          <div style="font-size:2rem;margin-bottom:1rem;">🔒</div>
          <h1 style="font-size:1.25rem;margin-bottom:.5rem;">${t('survey.public.notFoundTitle')}</h1>
          <p style="color:var(--color-text-muted,#64748b);">${t('survey.public.notFoundBody')}</p>`;

      case 'error':
        return `
          <div style="font-size:2rem;margin-bottom:1rem;">⚠️</div>
          <h1 style="font-size:1.25rem;margin-bottom:.5rem;">${t('survey.public.errorTitle')}</h1>
          <p style="color:var(--color-text-muted,#64748b);">${t('survey.public.errorBody')}</p>`;

      case 'answered':
        return `
          <div style="font-size:2.5rem;margin-bottom:1rem;">✅</div>
          <h1 style="font-size:1.25rem;margin-bottom:.5rem;">${t('survey.public.answeredTitle')}</h1>
          <p style="color:var(--color-text-muted,#64748b);">${t('survey.public.answeredBody', { year: this.survey!.year })}</p>`;

      case 'submitted':
        return `
          <div style="font-size:2.5rem;margin-bottom:1rem;">🙏</div>
          <h1 style="font-size:1.25rem;margin-bottom:.5rem;">${t('survey.public.submittedTitle')}</h1>
          <p style="color:var(--color-text-muted,#64748b);">${t('survey.public.submittedBody', { landlordName: this.survey!.landlordDisplayName })}</p>`;

      case 'form':
        return `
          <div style="font-size:2rem;margin-bottom:1rem;">🏠</div>
          <p style="color:var(--color-text-muted,#64748b);font-size:.875rem;margin-bottom:.25rem;">${this.survey!.landlordDisplayName} — ${this.survey!.year}</p>
          <h1 style="font-size:1.2rem;font-weight:600;margin-bottom:1.5rem;">${t('survey.public.formTitle')}</h1>
          <div id="star-row" style="display:flex;justify-content:center;gap:.5rem;margin-bottom:1.5rem;">
            ${[1, 2, 3, 4, 5].map((n) => `
              <button
                type="button"
                data-star="${n}"
                style="background:none;border:2px solid ${this.selectedRating && n <= this.selectedRating ? '#f59e0b' : '#e2e8f0'};border-radius:8px;padding:.6rem .8rem;font-size:1.5rem;cursor:pointer;transition:border-color .15s,transform .1s;color:${this.selectedRating && n <= this.selectedRating ? '#f59e0b' : '#cbd5e1'};"
                title="${t('survey.public.starLabel', { count: n })}"
                aria-label="${t('survey.public.starLabel', { count: n })}">
                ★
              </button>`).join('')}
          </div>
          <textarea
            id="survey-comment"
            rows="3"
            placeholder="${t('survey.public.commentPlaceholder')}"
            style="width:100%;box-sizing:border-box;border:1px solid #e2e8f0;border-radius:8px;padding:.75rem;font-size:.9rem;resize:vertical;font-family:inherit;margin-bottom:1rem;"></textarea>
          ${this.errorMessage ? `<p style="color:#ef4444;font-size:.875rem;margin-bottom:.75rem;">${this.errorMessage}</p>` : ''}
          <button
            id="survey-submit"
            type="button"
            style="width:100%;background:var(--color-primary,#2563eb);color:#fff;border:none;border-radius:8px;padding:.75rem 1.5rem;font-size:1rem;font-weight:600;cursor:${this.submitting ? 'not-allowed' : 'pointer'};opacity:${this.submitting ? '.7' : '1'};"
            ${this.submitting ? 'disabled' : ''}>
            ${this.submitting ? t('survey.public.submitting') : t('survey.public.submit')}
          </button>`;

      default:
        return '';
    }
  }

  private attachFormListeners(): void {
    const stars = this.querySelectorAll<HTMLButtonElement>('[data-star]');
    stars.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.selectedRating = Number(btn.dataset.star);
        this.errorMessage = '';
        this.render();
      });
    });

    this.querySelector('#survey-submit')?.addEventListener('click', () => {
      void this.handleSubmit();
    });
  }

  private async handleSubmit(): Promise<void> {
    if (!this.selectedRating) {
      this.errorMessage = t('survey.public.ratingError');
      this.render();
      return;
    }

    const comment = (this.querySelector('#survey-comment') as HTMLTextAreaElement | null)?.value.trim() || null;

    this.submitting = true;
    this.errorMessage = '';
    this.render();

    try {
      await satisfactionSurveysApi.submit(this.token!, { rating: this.selectedRating, comment });
      this.state = 'submitted';
    } catch (err: unknown) {
      this.state = 'form';
      this.errorMessage = err instanceof Error ? err.message : t('common.errorGeneric');
    } finally {
      this.submitting = false;
    }

    this.render();
  }
}

if (!customElements.get('rc-survey')) {
  customElements.define('rc-survey', RcSurvey);
}
